import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useCart } from "../hooks/useCart";
import { useUnreadContactCount } from "../hooks/useUnreadContactCount";
import { PHONE_NUMBER, PHONE_TEL } from "../lib/contact";
import { trackButtonClick, trackEvent } from "../lib/analytics";

function AdminDropdown({ isActive, unreadCount }: { isActive: (path: string) => boolean; unreadCount: number }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const adminActive = isActive("/admin") || isActive("/warehouse");

  const links = [
    { to: "/admin", label: "Dashboard" },
    { to: "/admin/leads", label: "Leads" },
    { to: "/admin/orders", label: "Order Fulfillment" },
    { to: "/warehouse/expiration", label: "Expiration Tracking" },
    { to: "/admin/products", label: "Products" },
    { to: "/admin/customers", label: "Customers" },
    { to: "/admin/auctions", label: "Auctions" },
    { to: "/admin/customer-pricing", label: "Customer Pricing" },
    { to: "/admin/pricing", label: "Pricing Rules" },
    { to: "/admin/bulk-upload", label: "Bulk Upload" },
    { to: "/admin/netsuite", label: "NetSuite" },
    { to: "/admin/advertising", label: "Advertising" },
    { to: "/admin/landing-pages", label: "Landing Pages" },
    { to: "/admin/content", label: "Content" },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`text-sm font-medium flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 rounded ${adminActive ? "text-accent" : "text-primary hover:text-accent"}`}
      >
        <span className="relative">
          Admin
          {unreadCount > 0 && (
            <span className="absolute -top-2 -right-3 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-0.5 leading-none">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </span>
        <svg className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {links.map(link => (
            <Link key={link.to} to={link.to} onClick={() => setOpen(false)}
              className={`block px-4 py-2 text-sm hover:bg-gray-50 ${isActive(link.to) ? "text-accent font-medium" : "text-gray-700"}`}>
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { cartCount, supplementalCount } = useCart();
  const totalCartCount = cartCount + supplementalCount;
  const { unreadContactCount } = useUnreadContactCount();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [auctionsEnabled, setAuctionsEnabled] = useState(false);

  useEffect(() => {
    fetch("/api/settings/auctions-enabled")
      .then(r => r.json())
      .then(data => setAuctionsEnabled(data.auctions_enabled !== false))
      .catch(() => setAuctionsEnabled(false));
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <nav aria-label="Main navigation" className="bg-white border-b border-gray-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.svg" alt="Premier Pharma" className="h-8" />
            </Link>

            <div className="hidden md:block w-px h-8 bg-gray-200" />

            <div className="hidden md:flex items-center gap-2">
              <Link
                to="/products"
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  isActive("/products")
                    ? "bg-blue-50 text-accent shadow-sm"
                    : "text-primary hover:bg-gray-50 hover:text-accent"
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                Products
              </Link>

              {auctionsEnabled && (
              <Link
                to="/auctions"
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  isActive("/auctions")
                    ? "bg-blue-50 text-accent shadow-sm"
                    : "text-primary hover:bg-gray-50 hover:text-accent"
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Auctions
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                </span>
              </Link>
              )}

              <Link
                to="/drugs"
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  isActive("/drugs")
                    ? "bg-blue-50 text-accent shadow-sm"
                    : "text-primary hover:bg-gray-50 hover:text-accent"
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
                Drug Directory
              </Link>

              <Link
                to="/insights"
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  isActive("/insights")
                    ? "bg-blue-50 text-accent shadow-sm"
                    : "text-primary hover:bg-gray-50 hover:text-accent"
                }`}
              >
                Insights
              </Link>

              <Link
                to="/forms"
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  isActive("/forms")
                    ? "bg-blue-50 text-accent shadow-sm"
                    : "text-primary hover:bg-gray-50 hover:text-accent"
                }`}
              >
                Forms
              </Link>

              {user && (
                <Link
                  to="/favorites"
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    isActive("/favorites")
                      ? "bg-blue-50 text-accent shadow-sm"
                      : "text-primary hover:bg-gray-50 hover:text-accent"
                  }`}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill={isActive("/favorites") ? "currentColor" : "none"} stroke="currentColor" strokeWidth={2}>
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                  </svg>
                  Favorites
                </Link>
              )}

              {user && user.role === "customer" && (
                <Link
                  to="/my-invoices"
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    isActive("/my-invoices")
                      ? "bg-blue-50 text-accent shadow-sm"
                      : "text-primary hover:bg-gray-50 hover:text-accent"
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  My Orders
                </Link>
              )}

              {user && user.role === "customer" && (
                <Link
                  to="/epcis"
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    isActive("/epcis")
                      ? "bg-blue-50 text-accent shadow-sm"
                      : "text-primary hover:bg-gray-50 hover:text-accent"
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  EPCIS Documents
                </Link>
              )}
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <div className="hidden lg:flex flex-col items-center gap-1">
              <a
                href={PHONE_TEL}
                onClick={() => trackEvent("phone_contact_clicked", { location: "header" })}
                className="text-sm text-gray-500 hover:text-accent transition-colors font-medium"
              >
                {PHONE_NUMBER}
              </a>
              <Link
                to="/contact"
                onClick={() => trackButtonClick("Contact Us", "header")}
                className="btn-primary text-xs !py-1 !px-3 whitespace-nowrap"
              >
                Contact Us
              </Link>
            </div>
            {user ? (
              <>
                <Link to="/cart" className="text-primary hover:text-accent text-sm font-medium relative flex items-center gap-1.5">
                  <span className="relative">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" />
                    </svg>
                    {totalCartCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-accent text-white text-[10px] font-bold rounded-full w-4.5 h-4.5 min-w-[18px] min-h-[18px] flex items-center justify-center leading-none">
                        {totalCartCount > 99 ? "99+" : totalCartCount}
                      </span>
                    )}
                  </span>
                  Cart
                </Link>
                {user.role === "admin" && (
                  <AdminDropdown isActive={isActive} unreadCount={unreadContactCount} />
                )}
                <Link to="/account" className="text-sm text-gray-500 hover:text-accent transition-colors">{user.firstName}</Link>
                <button onClick={handleLogout} className="btn-outline text-sm !py-2 !px-4">Logout</button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-outline text-sm !py-2 !px-4">Login</Link>
                <Link to="/register" className="btn-primary text-sm !py-2 !px-4">Sign Up</Link>
              </>
            )}
          </div>

          <button
            className="md:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 rounded"
            aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <svg aria-hidden="true" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {mobileOpen && (
          <div id="mobile-nav" className="md:hidden py-4 border-t border-gray-100">
            <div className="flex flex-col gap-1">
              <Link
                to="/products"
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive("/products") ? "bg-blue-50 text-accent" : "text-primary hover:bg-gray-50"
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                Products
              </Link>
              {auctionsEnabled && (
              <Link
                to="/auctions"
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive("/auctions") ? "bg-blue-50 text-accent" : "text-primary hover:bg-gray-50"
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Auctions
                <span className="relative flex h-2 w-2 ml-1">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
              </Link>
              )}
              <Link
                to="/drugs"
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive("/drugs") ? "bg-blue-50 text-accent" : "text-primary hover:bg-gray-50"
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
                Drug Directory
              </Link>
              <Link
                to="/insights"
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive("/insights") ? "bg-blue-50 text-accent" : "text-primary hover:bg-gray-50"
                }`}
              >
                Insights
              </Link>
              <Link
                to="/forms"
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive("/forms") ? "bg-blue-50 text-accent" : "text-primary hover:bg-gray-50"
                }`}
              >
                Forms
              </Link>
              {user && (
                <Link
                  to="/favorites"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive("/favorites") ? "bg-blue-50 text-accent" : "text-primary hover:bg-gray-50"
                  }`}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill={isActive("/favorites") ? "currentColor" : "none"} stroke="currentColor" strokeWidth={2}>
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                  </svg>
                  Favorites
                </Link>
              )}
              {user && user.role === "admin" && (
                <>
                  <div className="px-3 py-1 flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Admin</span>
                    {unreadContactCount > 0 && (
                      <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-0.5 leading-none">
                        {unreadContactCount > 99 ? "99+" : unreadContactCount}
                      </span>
                    )}
                  </div>
                  {[
                    { to: "/admin", label: "Dashboard" },
                    { to: "/admin/leads", label: "Leads" },
                    { to: "/admin/orders", label: "Orders" },
                    { to: "/admin/products", label: "Products" },
                    { to: "/admin/customers", label: "Customers" },
                    { to: "/admin/auctions", label: "Auctions" },
                    { to: "/admin/customer-pricing", label: "Customer Pricing" },
                    { to: "/admin/pricing", label: "Pricing Rules" },
                    { to: "/admin/bulk-upload", label: "Bulk Upload" },
                    { to: "/admin/netsuite", label: "NetSuite" },
                    { to: "/admin/advertising", label: "Advertising" },
                     { to: "/admin/landing-pages", label: "Landing Pages" },
                     { to: "/admin/content", label: "Content" },
                    { to: "/warehouse/expiration", label: "Expiration" },
                  ].map(link => (
                    <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}
                      className={`block px-3 py-2 rounded-lg text-sm font-medium ${isActive(link.to) ? "bg-blue-50 text-accent" : "text-primary hover:bg-gray-50"}`}>
                      {link.label}
                    </Link>
                  ))}
                  <div className="h-px bg-gray-100 my-1" />
                </>
              )}
              {user && user.role === "customer" && (
                <Link
                  to="/my-invoices"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive("/my-invoices") ? "bg-blue-50 text-accent" : "text-primary hover:bg-gray-50"
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  My Orders
                </Link>
              )}
              {user && user.role === "customer" && (
                <Link
                  to="/epcis"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive("/epcis") ? "bg-blue-50 text-accent" : "text-primary hover:bg-gray-50"
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  EPCIS Documents
                </Link>
              )}
              <div className="h-px bg-gray-100 my-2" />
              {user ? (
                <>
                  <Link to="/cart" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 text-primary text-sm font-medium rounded-lg hover:bg-gray-50">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" />
                    </svg>
                    Cart
                    {totalCartCount > 0 && (
                      <span className="bg-accent text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                        {totalCartCount > 99 ? "99+" : totalCartCount}
                      </span>
                    )}
                  </Link>
                  <Link to="/account" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 text-primary text-sm font-medium rounded-lg hover:bg-gray-50">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Account
                  </Link>
                  <button
                    onClick={() => { setMobileOpen(false); handleLogout(); }}
                    className="flex items-center gap-2 px-3 py-2.5 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileOpen(false)} className="btn-outline text-sm !py-2 !px-4 text-center">Login</Link>
                  <Link to="/register" onClick={() => setMobileOpen(false)} className="btn-primary text-sm !py-2 !px-4 text-center">Sign Up</Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
